import React from 'react';
import {
  AlertTriangleIcon,
  LinkBrokenIcon,
  PasscodeLockIcon,
  PhoneIcon,
  ShieldTickIcon,
  XCircleIcon,
} from './transactional-icons';

export type SecureInputStageKind =
  | 'signing-in'
  | 'approve'
  | 'signed-in'
  | 'cancelled'
  | 'expired'
  | 'error';

// 32px render of a 24-unit icon: 2px on screen is 1.5 viewBox units.
const STAGE_ICON_SIZE = 32;
const STAGE_ICON_STROKE = (2 * 24) / STAGE_ICON_SIZE;

const STAGE_ICONS: Record<
  SecureInputStageKind,
  React.FC<{
    size?: number;
    strokeWidth?: number;
  }>
> = {
  'signing-in': PasscodeLockIcon,
  approve: PhoneIcon,
  'signed-in': ShieldTickIcon,
  cancelled: XCircleIcon,
  expired: LinkBrokenIcon,
  error: AlertTriangleIcon,
};

/** Indeterminate progress: four dots travel right → left, easing through the middle. */
export const DotsLoader: React.FC = () => (
  <div className='dots-loader' role='progressbar' aria-label='Working'>
    {[0, 1, 2, 3].map((i) => (
      <span key={i} className='dots-loader-dot' />
    ))}
  </div>
);

interface SecureInputStageProps {
  kind: SecureInputStageKind;
  title: React.ReactNode;
  titleId: string;
  /** Shows the dots loader (with `waiting` copy above it, if given). */
  busy?: boolean;
  waiting?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * A post-form step of the secure sign-in: accent icon, heading, copy and
 * (while waiting) a centred countdown + dots loader. The caller keys this by
 * stage so every stage change remounts it and replays the enter animation.
 */
const SecureInputStage: React.FC<SecureInputStageProps> = ({
  kind,
  title,
  titleId,
  busy,
  waiting,
  children,
}) => {
  const Icon = STAGE_ICONS[kind];
  return (
    <div className={`stage stage--${kind}`}>
      <span className='stage-icon' aria-hidden='true'>
        <Icon size={STAGE_ICON_SIZE} strokeWidth={STAGE_ICON_STROKE} />
      </span>
      <div className='stage-text'>
        <h2 className='tx-title hero' id={titleId}>
          {title}
        </h2>
        {children}
      </div>
      {busy && (
        <div className='stage-wait'>
          {waiting && (
            <p className='fine' role='timer'>
              {waiting}
            </p>
          )}
          <DotsLoader />
        </div>
      )}
    </div>
  );
};

export default SecureInputStage;
